import { useMemo } from "react";
import type { DefaultValuesOption } from "@stripe/stripe-js";
import { useFormContext } from "react-hook-form";
import { usePaywallCheckoutContext } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import type { CheckoutFormValuesProps } from "../../CheckoutFormValuesProps";
import { getCurrentUserName } from "../../helpers/users";

export const usePaymentElementDefaultValues = (): DefaultValuesOption => {
  const { watch } = useFormContext<CheckoutFormValuesProps>();
  const { currentUser, memberBillingInfo } = usePaywallCheckoutContext();

  const emailFieldValue = watch("email");
  const nameFieldValue = watch("name");

  return useMemo(() => {
    // If there is a community member logged in, we prefill the Payment Element with their email
    // and name.
    //
    // Otherwise, we use the values from the email and name fields in our form to force the
    // Payment Element to re-render and avoid showing the email and name fields as we have dedicated
    // fields for them in our form.
    const email = currentUser?.email ?? emailFieldValue;
    const name = getCurrentUserName(currentUser) ?? nameFieldValue;

    // If there is a community member logged in that has already provided an address, we prefill
    // the Payment Element with it.
    //
    // Otherwise, we rely on the Address Element to provide the Payment Element with the address
    // values it requires to confirm the payment.
    let address:
      | NonNullable<DefaultValuesOption["billingDetails"]>["address"]
      | undefined;
    if (memberBillingInfo?.address_country) {
      address = {
        line1: memberBillingInfo.address_line1,
        line2: memberBillingInfo.address_line2,
        city: memberBillingInfo.address_city,
        state: memberBillingInfo.address_state,
        postal_code: memberBillingInfo.address_postal_code,
        country: memberBillingInfo.address_country,
      };
    }

    return {
      billingDetails: {
        email,
        name,
        address,
      },
    };
  }, [currentUser, memberBillingInfo, emailFieldValue, nameFieldValue]);
};
